import { useState, useMemo, useEffect } from 'react';
import { TranscriptionResult, ProAnalysisResult, WordStatus } from '../types';
import {
  getPhonemeBreakdown,
  getPronunciationTips,
  analyzeTimings,
  getWordDifficulty,
  fetchWordPhonemes
} from '../utils/phonemeAnalysis';
import { calculateLevenshteinDistance, getMatchQuality } from '../utils/levenshtein';
import {
  PhonemeBreakdown,
  AnalyzedWord,
  WordStats,
  TimingAnalysisResult
} from '../components/pro-feedback/types';

interface UseProAnalysisProps {
  targetText: string;
  transcriptionResult: TranscriptionResult | null;
  proAnalysis?: ProAnalysisResult;
}

interface UseProAnalysisReturn {
  analyzedWords: AnalyzedWord[];
  stats: WordStats;
  overallScore: number;
  avgConfidence: number;
  timingAnalysis: TimingAnalysisResult | null;
  azureAnalysis: {
    accuracy: number;
    fluency: number;
    completeness: number;
    prosodyScore?: number;
    tips: string[];
  } | null;
  phonemesLoading: boolean;
  getWordPhonemesDisplay: (word: string) => PhonemeBreakdown[];
}

export function useProAnalysis({
  targetText,
  transcriptionResult,
  proAnalysis
}: UseProAnalysisProps): UseProAnalysisReturn {
  const [phonemesLoading, setPhonemesLoading] = useState<boolean>(true);
  const [enhancedPhonemes, setEnhancedPhonemes] = useState<Map<string, PhonemeBreakdown[]> | null>(null);

  // Analyze words - use proAnalysis if available (Azure Speech), otherwise use Levenshtein
  const { analyzedWords, stats, overallScore, avgConfidence, timingAnalysis, azureAnalysis } = useMemo(() => {
    // If we have proAnalysis from Azure Speech, use that
    if (proAnalysis) {
      const analyzed: AnalyzedWord[] = proAnalysis.wordAnalysis.map(w => {
        // Map Azure status to WordStatus
        let status: WordStatus;
        switch (w.status) {
          case 'correct': status = 'correct'; break;
          case 'partial': status = 'unclear'; break;
          case 'incorrect': status = 'substitution'; break;
          case 'missed': status = 'omission'; break;
          case 'extra': status = 'insertion'; break;
          default: status = 'unclear';
        }

        // Use Azure accuracy score for confidence
        const confidence = w.accuracyScore / 100;

        return {
          word: w.target || w.spoken,
          status,
          confidence,
          transcribed: w.spoken,
          phonemes: getPhonemeBreakdown(w.target || w.spoken),
          difficulty: getWordDifficulty(w.target || w.spoken),
          tips: getPronunciationTips(w.target || w.spoken, confidence, w.status === 'correct'),
          azurePhonemes: w.phonemes,
          azureSyllables: w.syllables,
        };
      });

      const statsCalc: WordStats = {
        correct: proAnalysis.wordAnalysis.filter(w => w.status === 'correct').length,
        unclear: proAnalysis.wordAnalysis.filter(w => w.status === 'partial').length,
        substitutions: proAnalysis.wordAnalysis.filter(w => w.status === 'incorrect').length,
        omissions: proAnalysis.wordAnalysis.filter(w => w.status === 'missed').length,
        insertions: proAnalysis.wordAnalysis.filter(w => w.status === 'extra').length,
        total: proAnalysis.wordAnalysis.filter(w => w.target).length
      };

      return {
        analyzedWords: analyzed,
        stats: statsCalc,
        overallScore: proAnalysis.overallScore,
        avgConfidence: proAnalysis.accuracy,
        timingAnalysis: {
          totalDurationSec: proAnalysis.totalDuration.toFixed(1),
          wordsPerMinute: proAnalysis.totalDuration > 0
            ? Math.round((proAnalysis.wordAnalysis.length / proAnalysis.totalDuration) * 60)
            : 0,
          fluencyScore: proAnalysis.fluency,
        },
        azureAnalysis: {
          accuracy: proAnalysis.accuracy,
          fluency: proAnalysis.fluency,
          completeness: proAnalysis.completeness,
          prosodyScore: proAnalysis.prosodyScore,
          tips: proAnalysis.tips,
        }
      };
    }

    // Fallback: use Levenshtein-based analysis
    if (!transcriptionResult) {
      return {
        analyzedWords: [],
        stats: { correct: 0, unclear: 0, substitutions: 0, omissions: 0, insertions: 0, total: 0 },
        overallScore: 0,
        avgConfidence: 0,
        timingAnalysis: null,
        azureAnalysis: null
      };
    }

    const targetWords = targetText.split(/\s+/).filter(w => w.length > 0);
    const transcribedWords = transcriptionResult.words || [];

    // Use Levenshtein algorithm to align words
    const levenshteinResult = calculateLevenshteinDistance(targetWords, transcribedWords);

    // Convert diffs to AnalyzedWord format
    const analyzed: AnalyzedWord[] = levenshteinResult.diffs.map(diff => {
      const transcribedWord = diff.transcribedIndex !== null
        ? transcribedWords[diff.transcribedIndex]
        : null;

      if (diff.operation === 'match') {
        const quality = getMatchQuality(diff.confidence);
        const isMatch = true;
        return {
          word: diff.targetWord!,
          status: quality as WordStatus,
          confidence: diff.confidence,
          transcribed: diff.transcribedWord!,
          start: transcribedWord?.start,
          end: transcribedWord?.end,
          duration: transcribedWord ? transcribedWord.end - transcribedWord.start : undefined,
          phonemes: getPhonemeBreakdown(diff.targetWord!),
          difficulty: getWordDifficulty(diff.targetWord!),
          tips: getPronunciationTips(diff.targetWord!, diff.confidence, isMatch)
        };
      }

      if (diff.operation === 'substitution') {
        return {
          word: diff.targetWord!,
          status: 'substitution' as WordStatus,
          confidence: diff.confidence,
          transcribed: diff.transcribedWord!,
          start: transcribedWord?.start,
          end: transcribedWord?.end,
          duration: transcribedWord ? transcribedWord.end - transcribedWord.start : undefined,
          phonemes: getPhonemeBreakdown(diff.targetWord!),
          difficulty: getWordDifficulty(diff.targetWord!),
          tips: getPronunciationTips(diff.targetWord!, diff.confidence, false)
        };
      }

      if (diff.operation === 'omission') {
        return {
          word: diff.targetWord!,
          status: 'omission' as WordStatus,
          confidence: 0,
          phonemes: getPhonemeBreakdown(diff.targetWord!),
          difficulty: getWordDifficulty(diff.targetWord!),
          tips: getPronunciationTips(diff.targetWord!, 0, false)
        };
      }

      // insertion
      return {
        word: diff.transcribedWord!,
        status: 'insertion' as WordStatus,
        confidence: diff.confidence,
        transcribed: diff.transcribedWord!,
        start: transcribedWord?.start,
        end: transcribedWord?.end,
        duration: transcribedWord ? transcribedWord.end - transcribedWord.start : undefined,
        phonemes: getPhonemeBreakdown(diff.transcribedWord!),
        difficulty: getWordDifficulty(diff.transcribedWord!),
        tips: []
      };
    });

    const statsCalc: WordStats = {
      correct: analyzed.filter(w => w.status === 'correct').length,
      unclear: analyzed.filter(w => w.status === 'unclear').length,
      substitutions: analyzed.filter(w => w.status === 'substitution').length,
      omissions: analyzed.filter(w => w.status === 'omission').length,
      insertions: analyzed.filter(w => w.status === 'insertion').length,
      total: targetWords.length
    };

    const score = statsCalc.total > 0
      ? Math.round((statsCalc.correct / statsCalc.total) * 100)
      : 0;

    const wordsWithConfidence = analyzed.filter(w => w.confidence > 0);
    const avgConf = wordsWithConfidence.length > 0
      ? Math.round((wordsWithConfidence.reduce((sum, w) => sum + w.confidence, 0) / wordsWithConfidence.length) * 100)
      : 0;

    const timing = analyzeTimings(transcribedWords, targetText);

    return {
      analyzedWords: analyzed,
      stats: statsCalc,
      overallScore: score,
      avgConfidence: avgConf,
      timingAnalysis: timing as TimingAnalysisResult,
      azureAnalysis: null
    };
  }, [targetText, transcriptionResult, proAnalysis]);

  // Fetch enhanced phonemes from API
  useEffect(() => {
    const loadEnhancedPhonemes = async () => {
      if (analyzedWords.length === 0) {
        setPhonemesLoading(false);
        return;
      }

      setPhonemesLoading(true);
      try {
        const words = analyzedWords.map(w => w.word);
        const phonemeMap = await fetchWordPhonemes(words);
        setEnhancedPhonemes(phonemeMap);
      } catch (error) {
        console.error('Failed to load enhanced phonemes:', error);
      } finally {
        setPhonemesLoading(false);
      }
    };

    loadEnhancedPhonemes();
  }, [analyzedWords]);

  // Get phonemes for a word - use enhanced if available, otherwise fall back to local
  const getWordPhonemesDisplay = (word: string): PhonemeBreakdown[] => {
    const normalized = word.toLowerCase().replace(/[^a-z']/g, '');
    if (enhancedPhonemes && enhancedPhonemes.has(normalized)) {
      return enhancedPhonemes.get(normalized)!;
    }
    return getPhonemeBreakdown(word);
  };

  return {
    analyzedWords,
    stats,
    overallScore,
    avgConfidence,
    timingAnalysis,
    azureAnalysis,
    phonemesLoading,
    getWordPhonemesDisplay
  };
}
